// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'footer' }),
      });
      setSubmitted(true);
      if (window.gtag) window.gtag('event', 'newsletter_signup', { source: 'footer' });
    } catch {
      setSubmitted(true); // silently succeed
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#181816] border-t border-white/[0.06] mt-auto">
      <div className="container-wide py-12">
        {/* Newsletter */}
        <div className="pb-10 mb-10 border-b border-white/[0.06]">
          <p className="text-sm font-bold text-[#E8E8E4] mb-1">Resta aggiornato</p>
          <p className="text-sm text-[#888780] mb-4">Tips finanziari settimanali, novità normative e nuovi calcolatori. Niente spam.</p>
          {submitted ? (
            <div className="flex items-center gap-2 text-[#3D7A52]" data-testid="footer-newsletter-success">
              <Check className="w-4 h-4" />
              <span className="text-sm font-medium">Perfetto! Ti terremo aggiornato.</span>
            </div>
          ) : (
            <form onSubmit={handleNewsletter} className="flex flex-col sm:flex-row gap-3 max-w-sm">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="La tua email"
                required
                className="flex-1 px-4 py-2.5 rounded-xl text-sm text-[#E8E8E4] placeholder-[#888780] outline-none focus:ring-1 focus:ring-[#C9A84C]/50"
                style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.08)' }}
                data-testid="footer-newsletter-email"
              />
              <button
                type="submit"
                disabled={loading}
                className="shrink-0 px-5 py-2.5 rounded-xl text-sm font-semibold transition-opacity hover:opacity-90"
                style={{ background: '#C9A84C', color: '#3A2800' }}
                data-testid="footer-newsletter-submit"
              >
                {loading ? '...' : 'Iscriviti'}
              </button>
            </form>
          )}
        </div>

        {/* Links grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="w-2 h-2 rounded-full bg-[#C9A84C]"></span>
              <span className="text-[#E8E8E4] font-semibold tracking-widest">SAPIENS</span>
            </div>
            <p className="text-xs text-[#888780] leading-relaxed">
              Educazione finanziaria indipendente in italiano. Nessuna pubblicità, nessun conflitto di interesse.
            </p>
          </div>

          {/* Piattaforma */}
          <div>
            <p className="text-xs font-semibold text-[#E8E8E4] uppercase tracking-wider mb-4">Piattaforma</p>
            <div className="space-y-2.5">
              <Link to="/percorsi" className="block text-sm text-[#888780] hover:text-[#E8E8E4] transition-colors">Percorsi</Link>
              <Link to="/calcolatori" className="block text-sm text-[#888780] hover:text-[#E8E8E4] transition-colors">Calcolatori</Link>
              <Link to="/glossario" className="block text-sm text-[#888780] hover:text-[#E8E8E4] transition-colors">Glossario</Link>
              <Link to="/guida-busta-paga" className="block text-sm text-[#888780] hover:text-[#E8E8E4] transition-colors">Guida Busta Paga</Link>
              <Link to="/guida-kid" className="block text-sm text-[#888780] hover:text-[#E8E8E4] transition-colors">Guida KID</Link>
              <Link to="/premium" className="block text-sm text-[#888780] hover:text-[#E8E8E4] transition-colors">Premium</Link>
            </div>
          </div>

          {/* Risorse */}
          <div>
            <p className="text-xs font-semibold text-[#E8E8E4] uppercase tracking-wider mb-4">Risorse</p>
            <div className="space-y-2.5">
              <Link to="/about" className="block text-sm text-[#888780] hover:text-[#E8E8E4] transition-colors">Chi siamo</Link>
              <Link to="/chi-siamo" className="block text-sm text-[#888780] hover:text-[#E8E8E4] transition-colors">Il progetto</Link>
              <Link to="/blog" className="block text-sm text-[#888780] hover:text-[#E8E8E4] transition-colors">Blog</Link>
              <Link to="/faq" className="block text-sm text-[#888780] hover:text-[#E8E8E4] transition-colors">FAQ</Link>
              <Link to="/truffe-finanziarie" className="block text-sm text-[#888780] hover:text-[#E8E8E4] transition-colors">Truffe Finanziarie</Link>
            </div>
          </div>

          {/* Legale — exactly 4 links (Deployment 12 — GDPR/legal compliance) */}
          <div data-testid="footer-legal-section">
            <p className="text-xs font-semibold text-[#E8E8E4] uppercase tracking-wider mb-4">Legale</p>
            <div className="space-y-2.5" data-testid="footer-legal-links">
              <Link to="/privacy" className="block text-sm text-[#888780] hover:text-[#E8E8E4] transition-colors" data-testid="footer-link-privacy">Privacy Policy</Link>
              <Link to="/cookie-policy" className="block text-sm text-[#888780] hover:text-[#E8E8E4] transition-colors" data-testid="footer-link-cookie">Cookie Policy</Link>
              <Link to="/termini" className="block text-sm text-[#888780] hover:text-[#E8E8E4] transition-colors" data-testid="footer-link-termini">Termini di Servizio</Link>
              <Link to="/disclaimer" className="block text-sm text-[#888780] hover:text-[#E8E8E4] transition-colors" data-testid="footer-link-disclaimer">Disclaimer</Link>
              <p className="text-xs text-[#888780] leading-relaxed pt-2 border-t border-white/[0.06]">
                I contenuti hanno scopo esclusivamente educativo. Non costituiscono consulenza finanziaria.
              </p>
              <a href="mailto:privacy@finsapiens.it" className="block text-sm text-[#888780] hover:text-[#E8E8E4] transition-colors">privacy@finsapiens.it</a>
            </div>
          </div>
        </div>

        {/* Bottom — 4 legal links exactly */}
        <div className="border-t border-white/[0.06] pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-[#888780]">
            © 2026 SAPIENS — Gian Marco Cappelli. Tutti i diritti riservati.
          </p>
          <div className="flex items-center gap-4" data-testid="footer-bottom-legal">
            <Link to="/privacy" className="text-xs text-[#888780] hover:text-[#E8E8E4] transition-colors">Privacy Policy</Link>
            <Link to="/cookie-policy" className="text-xs text-[#888780] hover:text-[#E8E8E4] transition-colors">Cookie Policy</Link>
            <Link to="/termini" className="text-xs text-[#888780] hover:text-[#E8E8E4] transition-colors">Termini di Servizio</Link>
            <Link to="/disclaimer" className="text-xs text-[#888780] hover:text-[#E8E8E4] transition-colors">Disclaimer</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
